import React, { useState, useEffect, useRef } from 'react';
import { parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import { Check, AlertCircle, Loader, MessageSquare, Send, Globe, ChevronDown, CheckSquare, Square } from 'lucide-react';
import api from '../../services/api';

const COUNTRIES = [
  { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪', placeholder: '0712 345678' },
  { code: 'UG', name: 'Uganda', dialCode: '+256', flag: '🇺🇬', placeholder: '0712 345678' },
  { code: 'TZ', name: 'Tanzania', dialCode: '+255', flag: '🇹🇿', placeholder: '0712 345678' },
  { code: 'ET', name: 'Ethiopia', dialCode: '+251', flag: '🇪🇹', placeholder: '0912 345678' },
  { code: 'RW', name: 'Rwanda', dialCode: '+250', flag: '🇷🇼', placeholder: '0782 345678' },
];

const detectCountryCode = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz.includes('Nairobi')) return 'KE';
    if (tz.includes('Kampala')) return 'UG';
    if (tz.includes('Dar_es_Salaam')) return 'TZ';
    if (tz.includes('Addis_Ababa')) return 'ET';
    if (tz.includes('Kigali')) return 'RW';
    if (tz.includes('London') || tz.includes('Europe/London')) return 'GB';
    if (tz.includes('America/')) return 'US';
  } catch (e) {}
  return 'KE'; // Default to Kenya as starter market focus
};

export default function FlowbotLaunchOptIn({ variant = 'default', whatsappTriggerCount = 0 }) {
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isValidPhone, setIsValidPhone] = useState(false);
  const [consentWhatsapp, setConsentWhatsapp] = useState(false);
  const [consentTelegram, setConsentTelegram] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const dropdownRef = useRef(null);
  const phoneInputRef = useRef(null);

  // Sync state on WhatsApp notification trigger
  useEffect(() => {
    if (whatsappTriggerCount > 0) {
      setConsentWhatsapp(true);
      setShowValidationErrors(true);
      setTimeout(() => {
        if (phoneInputRef.current) {
          phoneInputRef.current.focus();
        }
      }, 100);
    }
  }, [whatsappTriggerCount]);

  // Auto-detect timezone
  useEffect(() => {
    const code = detectCountryCode();
    const match = COUNTRIES.find(c => c.code === code);
    if (match) {
      setSelectedCountry(match);
    }
  }, []);

  // Dropdown click outside listener
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle phone change
  const handlePhoneChange = (e) => {
    const inputVal = e.target.value;
    
    // Strip non-numeric and non-plus characters
    const numericVal = inputVal.replace(/[^\d+]/g, '');

    // Format with AsYouType helper
    const formatted = new AsYouType(selectedCountry.code).input(numericVal);
    setPhoneNumber(formatted);

    // Validate in real-time
    const parsed = parsePhoneNumberFromString(numericVal, selectedCountry.code);
    const valid = parsed ? parsed.isValid() : false;
    setIsValidPhone(valid);
  };

  // Strip formatting on blur
  const handleBlur = () => {
    const parsed = parsePhoneNumberFromString(phoneNumber, selectedCountry.code);
    if (parsed && parsed.isValid()) {
      setPhoneNumber(parsed.formatNational());
    }
  };

  // Filter countries list
  const filteredCountries = COUNTRIES.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.dialCode.includes(searchQuery) ||
    c.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhone || (!consentWhatsapp && !consentTelegram)) {
      setShowValidationErrors(true);
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      const parsed = parsePhoneNumberFromString(phoneNumber, selectedCountry.code);
      const e164Number = parsed ? parsed.format('E.164') : '';

      const channels = [];
      if (consentWhatsapp) channels.push('whatsapp');
      if (consentTelegram) channels.push('telegram');

      const response = await api.submitLaunchSignup({
        phone: e164Number,
        channels: channels,
        country_code: selectedCountry.code,
        consent_text_shown: {
          whatsapp: consentWhatsapp ? "I consent to receiving WhatsApp messages from Forwardsflow." : undefined,
          telegram: consentTelegram ? "I consent to receiving Telegram messages from Forwardsflow." : undefined
        },
        timestamp_client: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
      });

      if (response && (response.success || response.message)) {
        setSuccess(true);
      } else {
        setErrorMsg(response.error || 'Failed to submit launch notification.');
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'An error occurred during submission.');
    } finally {
      setLoading(false);
    }
  };

  // Check if form is valid
  const isFormValid = isValidPhone && (consentWhatsapp || consentTelegram);
  const showConsentError = showValidationErrors && !consentWhatsapp && !consentTelegram;

  return (
    <div className={`w-full ${variant === 'stretched' ? 'max-w-5xl' : 'max-w-lg'} bg-zinc-50 border border-zinc-200 p-8 mt-12 mx-auto text-left shadow-sm`}>
      {success ? (
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-none flex items-center justify-center mx-auto mb-4 border border-emerald-200">
            <Check className="w-6 h-6" />
          </div>
          <h3 className="font-sans text-lg font-bold text-black mb-2">You're on the list!</h3>
          <p className="font-sans text-xs text-zinc-600">
            We'll message you when we go live. Check your chosen channel(s) for a confirmation.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={`space-y-6 ${variant === 'stretched' ? 'grid md:grid-cols-2 gap-x-8 gap-y-4 space-y-0' : ''}`}>
          <div className={variant === 'stretched' ? 'md:col-span-2' : ''}>
            <h3 className="font-sans text-xl font-bold text-black mb-1">Be the first to know when we launch</h3>
            <p className="font-sans text-xs text-zinc-600">
              Drop your number and we'll ping you the moment Forwardsflow goes live.
            </p>
          </div>

          {errorMsg && (
            <div className={`bg-red-50 border border-red-200 p-3 flex gap-2 text-red-700 text-xs font-sans ${variant === 'stretched' ? 'md:col-span-2' : ''}`}>
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          <div className="space-y-2">
            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Phone Number</label>
            <div className="flex gap-2 relative">
              {/* Custom Searchable Dropdown */}
              <div ref={dropdownRef} className="w-32 flex-shrink-0 relative">
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-full h-11 bg-white border border-zinc-300 px-3 flex items-center justify-between font-mono text-xs text-zinc-800 hover:border-zinc-400 focus:outline-none"
                >
                  <span className="truncate">{selectedCountry.flag} {selectedCountry.dialCode}</span>
                  <ChevronDown className="w-3 h-3 text-zinc-500" />
                </button>

                {isDropdownOpen && (
                  <div className="absolute left-0 right-0 mt-1 bg-white border border-zinc-300 shadow-lg z-50 max-h-60 overflow-y-auto">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-2 py-1.5 border-b border-zinc-200 font-mono text-xs focus:outline-none"
                    />
                    <div className="py-1">
                      {filteredCountries.map((c) => (
                        <button
                          key={c.code}
                          type="button"
                          onClick={() => {
                            setSelectedCountry(c);
                            setIsDropdownOpen(false);
                            setSearchQuery('');
                            // Revalidate with new dialCode
                            const parsed = parsePhoneNumberFromString(phoneNumber, c.code);
                            setIsValidPhone(parsed ? parsed.isValid() : false);
                          }}
                          className="w-full px-3 py-2 text-left font-mono text-xs hover:bg-zinc-50 flex items-center gap-2"
                        >
                          <span>{c.flag}</span>
                          <span className="font-bold text-zinc-800">{c.code}</span>
                          <span className="text-zinc-500 truncate">({c.dialCode})</span>
                        </button>
                      ))}
                      {filteredCountries.length === 0 && (
                        <div className="px-3 py-2 text-zinc-400 text-xs font-mono">No countries found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Number Input field */}
              <div className="flex-1 relative">
                <input
                  ref={phoneInputRef}
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  onBlur={handleBlur}
                  placeholder={selectedCountry.placeholder}
                  className={`w-full h-11 bg-white border px-3 pr-10 font-mono text-xs text-black focus:outline-none ${
                    showValidationErrors && !isValidPhone
                      ? 'border-red-500 focus:border-red-600'
                      : phoneNumber
                        ? isValidPhone
                          ? 'border-emerald-500 focus:border-emerald-600'
                          : 'border-red-400 focus:border-red-500'
                        : 'border-zinc-300 focus:border-zinc-400'
                  }`}
                  required
                />
                {phoneNumber && (
                  <div className="absolute right-3 top-3.5">
                    {isValidPhone ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Consent Checkboxes */}
          <div className="space-y-2">
            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Select Consent Method(s)</label>
            {variant === 'stretched' ? (
              <div className={`flex flex-col justify-between h-11 ${showConsentError ? 'border border-red-500 px-2' : ''}`}>
                <button
                  type="button"
                  onClick={() => setConsentWhatsapp(!consentWhatsapp)}
                  className="flex items-center gap-2.5 text-left font-sans text-xs text-zinc-800 focus:outline-none group select-none h-5"
                >
                  <div className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center border border-zinc-400 bg-white group-hover:border-zinc-600">
                    {consentWhatsapp && <Check className="w-3 h-3 text-black stroke-[3]" />}
                  </div>
                  <span className="text-[11px] leading-none">I consent to receiving <strong className="font-bold">WhatsApp</strong> messages from Forwardsflow.</span>
                </button>

                <button
                  type="button"
                  onClick={() => setConsentTelegram(!consentTelegram)}
                  className="flex items-center gap-2.5 text-left font-sans text-xs text-zinc-800 focus:outline-none group select-none h-5"
                >
                  <div className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center border border-zinc-400 bg-white group-hover:border-zinc-600">
                    {consentTelegram && <Check className="w-3 h-3 text-black stroke-[3]" />}
                  </div>
                  <span className="text-[11px] leading-none">I consent to receiving <strong className="font-bold">Telegram</strong> messages from Forwardsflow.</span>
                </button>
              </div>
            ) : (
              <div className={`space-y-2 p-2 border ${showConsentError ? 'border-red-500' : 'border-transparent'}`}>
                <button
                  type="button"
                  onClick={() => setConsentWhatsapp(!consentWhatsapp)}
                  className="flex items-center gap-3 text-left font-sans text-xs text-zinc-800 focus:outline-none group select-none"
                >
                  <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center border border-zinc-400 bg-white group-hover:border-zinc-600">
                    {consentWhatsapp && <Check className="w-3.5 h-3.5 text-black stroke-[3]" />}
                  </div>
                  <span>I consent to receiving <strong className="font-bold">WhatsApp</strong> messages from Forwardsflow.</span>
                </button>

                <button
                  type="button"
                  onClick={() => setConsentTelegram(!consentTelegram)}
                  className="flex items-center gap-3 text-left font-sans text-xs text-zinc-800 focus:outline-none group select-none"
                >
                  <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center border border-zinc-400 bg-white group-hover:border-zinc-600">
                    {consentTelegram && <Check className="w-3.5 h-3.5 text-black stroke-[3]" />}
                  </div>
                  <span>I consent to receiving <strong className="font-bold">Telegram</strong> messages from Forwardsflow.</span>
                </button>
              </div>
            )}
            
            {variant !== 'stretched' && (
              <p className="text-[10px] font-sans text-zinc-500 leading-relaxed pt-2">
                *At least one consent option must be chosen. You can unsubscribe at any time by replying STOP or contacting us. See our <a href="#privacy" className="underline hover:text-black">Privacy Policy</a>.
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className={variant === 'stretched' ? 'md:col-span-2' : ''}>
            <button
              type="submit"
              disabled={!isFormValid || loading}
              className={`w-full h-12 font-mono text-xs uppercase tracking-[0.15em] font-semibold border transition-all flex items-center justify-center gap-2 ${
                isFormValid
                  ? 'bg-black border-black text-white hover:bg-zinc-800 hover:border-zinc-800 cursor-pointer'
                  : 'bg-zinc-200 border-zinc-200 text-zinc-400 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin text-zinc-400" />
                  Processing...
                </>
              ) : (
                'Notify Me at Launch'
              )}
            </button>
            {variant === 'stretched' && (
              <p className="text-[10px] font-sans text-zinc-500 leading-relaxed pt-2 mt-2">
                *At least one consent option must be chosen. You can unsubscribe at any time by replying STOP or contacting us. See our <a href="#privacy" className="underline hover:text-black">Privacy Policy</a>.
              </p>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
